/**
 * Units ⇄ cases on the forecast grid.
 *
 * A plan row stores cases (and dollars); units are a view of the same quantity through the
 * item's pack size (`metadata.units_per_case`, written by the SKU picker and the catalog add —
 * NetSuite's "Case Quantity" vs "Total Case Eaches"). Rows the actualizer wrote carry their own
 * `units_sold`, which wins when present. Everything that converts one way or the other goes
 * through this file so the grid, the edit path and the roll-ups agree on the arithmetic.
 */
import { aggregateChildrenToParents } from "./aggregation"
import { select_primary_sales_rows } from "./forecast_measure_slice"
import { cases_from_units, node_units_per_case, units_from_cases } from "./quantity_basis"

/** Why a Units cell shows an em dash: the tooltip the grid puts on it. */
export const UNITS_PER_CASE_UNKNOWN_HINT = "Set units per case on the item to show units"

interface UnitsNodeLike {
  metadata?: { units_per_case?: unknown; [key: string]: unknown } | null
  children?: UnitsNodeLike[]
  sales_data?: Array<{
    metrics?: { cases_sold?: number | null; units_sold?: number | null; detail?: Record<string, number> } | null
    source_kind?: string
    is_actual?: boolean
    period?: { period_key?: string }
  }>
}

export { cases_from_units, node_units_per_case, units_from_cases }

function point_has_units(point: NonNullable<UnitsNodeLike["sales_data"]>[number]): boolean {
  const m = point.metrics
  if (!m) return false
  if (typeof m.units_sold === "number" && Number.isFinite(m.units_sold)) return true
  const d = m.detail
  if (!d) return false
  return Object.keys(d).some((key: string) => key.endsWith("_units") && typeof d[key] === "number" && d[key] !== 0)
}

/**
 * Display pass: give every leaf whose rows carry no unit count a derived one
 * (`cases × units_per_case`) and re-roll the parents, so the Units toggle has something to show
 * on a plan that was only ever typed in cases. Returns the same array when nothing needed
 * filling, so a plan already carrying units costs nothing here. Runs on the render copy, never
 * on the saved data — the saved row stays in cases.
 */
export function fill_derived_units_in_hierarchy<T extends UnitsNodeLike>(roots: T[]): T[] {
  let filled = false

  function visit(node: T): T {
    const children = node.children as T[] | undefined
    if (Array.isArray(children) && children.length > 0) {
      const next_children = children.map(visit)
      const changed = next_children.some((child: T, index: number) => child !== children[index])
      return changed ? { ...node, children: next_children } : node
    }
    const units_per_case = node_units_per_case(node)
    if (!units_per_case) return node
    const rows = node.sales_data ?? []
    const primary = new Set(select_primary_sales_rows(rows as any))
    let touched = false
    const next_rows = rows.map((point) => {
      if (!primary.has(point as any) || point_has_units(point)) return point
      const units = units_from_cases(Number(point.metrics?.cases_sold ?? 0), units_per_case)
      if (units === null) return point
      touched = true
      return { ...point, metrics: { ...(point.metrics ?? {}), units_sold: units } }
    })
    if (!touched) return node
    filled = true
    return { ...node, sales_data: next_rows }
  }

  const next = roots.map(visit)
  if (!filled) return roots
  return aggregateChildrenToParents(next as any[]) as T[]
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("units_from_cases / cases_from_units", () => {
    it("converts both ways through the pack size, cases to the hundredth", () => {
      expect(units_from_cases(1.5, 12)).toBe(18)
      expect(units_from_cases(10, 6)).toBe(60)
      expect(cases_from_units(18, 12)).toBe(1.5)
      expect(cases_from_units(100, 12)).toBe(8.33)
      expect(cases_from_units(60, 6)).toBe(10)
    })

    it("says nothing when the pack size is unknown", () => {
      expect(units_from_cases(10, null)).toBeNull()
      expect(units_from_cases(10, 0)).toBeNull()
      expect(cases_from_units(10, undefined)).toBeNull()
      expect(cases_from_units(10, -3)).toBeNull()
    })
  })

  describe("node_units_per_case", () => {
    it("reads a positive pack size off the node's metadata and nothing else", () => {
      expect(node_units_per_case({ metadata: { units_per_case: 12 } })).toBe(12)
      expect(node_units_per_case({ metadata: { units_per_case: "6" } })).toBe(6)
      expect(node_units_per_case({ metadata: { units_per_case: 0 } })).toBeNull()
      expect(node_units_per_case({ metadata: null })).toBeNull()
      expect(node_units_per_case(undefined)).toBeNull()
    })
  })

  describe("fill_derived_units_in_hierarchy", () => {
    function leaf(id: string, units_per_case: number | undefined, cases: number, units?: number | null) {
      return {
        id,
        name: id,
        level: "sku",
        metadata: units_per_case ? { units_per_case } : null,
        children: [],
        sales_data: [
          {
            period: { period_key: "2027-01", year: 2027, month: 1 },
            metrics: { store_count: 10, volume_dollars: 100, cases_sold: cases, case_cost: 10, ...(units !== undefined ? { units_sold: units } : {}) },
            is_forecast: true,
          },
        ],
      }
    }

    it("derives units on leaves that have a pack size and rolls them up to the parent", () => {
      const roots = [{ id: "c", name: "Kroger", level: "customer", children: [leaf("a", 12, 2.5), leaf("b", 6, 4)], sales_data: [] }]
      const out = fill_derived_units_in_hierarchy(roots as any) as any
      expect(out).not.toBe(roots)
      expect(out[0].children[0].sales_data[0].metrics.units_sold).toBe(30)
      expect(out[0].children[1].sales_data[0].metrics.units_sold).toBe(24)
      expect((out[0] as any).sales_data[0].metrics.units_sold).toBe(54)
    })

    it("leaves a stored unit count alone and returns the same array when nothing is derived", () => {
      const roots = [{ id: "c", name: "Kroger", level: "customer", children: [leaf("a", 12, 2.5, 7), leaf("b", undefined, 4)], sales_data: [] }]
      const out = fill_derived_units_in_hierarchy(roots as any) as any
      expect(out).toBe(roots)
      expect(out[0].children[0].sales_data[0].metrics.units_sold).toBe(7)
    })

    it("does not touch the caller's rows", () => {
      const roots = [{ id: "c", name: "Kroger", level: "customer", children: [leaf("a", 12, 1)], sales_data: [] }]
      fill_derived_units_in_hierarchy(roots as any)
      expect(roots[0].children[0].sales_data[0].metrics).not.toHaveProperty("units_sold")
    })
  })
}
